import React, { useState, useEffect, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ConfettiExplosion from 'react-confetti-explosion';
import { CheckCircleIcon, ClipboardListIcon } from '@heroicons/react/solid';
import { XIcon, DocumentTextIcon } from '@heroicons/react/outline';

/**
 * RmsTriageCompleteModal
 * ----------------------
 * Purpose:
 *   Modal displayed when a guardian completes all 7 triage forms ("Additional Information").
 *   Shows celebration confetti and explains next steps - the referral is now ready for
 *   clinical review by the provider organisation.
 * 
 * Business Context:
 *   - Triage forms are assigned to guardians after the referral is accepted
 *   - These 7 forms collect comprehensive developmental, medical, and family history
 *   - Completing them allows clinicians to prepare for the assessment
 *   - This modal provides closure and sets expectations for what happens next
 * 
 * Design:
 *   - Uses RMS brand colours (primary teal)
 *   - Confetti celebration on modal open
 *   - Minimalist, consistent with existing RmsAllFormsCompletedModal
 *   - Shows "Access HFP Profile" CTA if user has completed all HFP forms too
 * 
 * Props:
 *   - isOpen: Whether the modal is visible
 *   - onClose: Callback when modal is closed
 *   - onViewHfp: Callback to open the HFP report dialog (optional)
 *   - orgName: Organisation name for the "next steps" message
 *   - patientName: CYP's name for personalization (optional)
 *   - hasCompletedHfp: Whether the guardian has also completed all HFP forms
 *   - isSecondaryReferral: Secondary-school referrals never produce an HFP, so
 *     the View HFP CTA is suppressed for them regardless of other props
 */

const customStyles = `
  @keyframes rms-fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes rms-scaleIn {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  
  .rms-animate-fadeIn {
    animation: rms-fadeIn 0.3s ease-out;
  }
  
  .rms-animate-scaleIn {
    animation: rms-scaleIn 0.3s ease-out;
  }
`;

export default function RmsTriageCompleteModal({
  isOpen,
  onClose,
  onViewHfp,
  orgName = 'the clinic',
  patientName,
  hasCompletedHfp = false,
  isSecondaryReferral = false,
}) {
  // Secondary referrals have no HFP document — never offer to view one, even
  // if a caller passes hasCompletedHfp (for secondary, "all forms complete"
  // means the referral forms alone, which is what tripped this before).
  const showHfpCta = Boolean(hasCompletedHfp && onViewHfp && !isSecondaryReferral);
  const [showConfetti, setShowConfetti] = useState(false);
  const modalContentRef = useRef(null);

  // Trigger confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const getCypName = () => patientName || 'the child or young person';

  return (
    <>
      <style>{customStyles}</style>
      
      <AlertDialog.Root open={isOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />
          <AlertDialog.Content 
            ref={modalContentRef}
            className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-lg max-h-[90vh] overflow-y-auto z-50"
          >
            <div className="relative">
              {/* Confetti explosion */}
              {showConfetti && (
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
                  <ConfettiExplosion 
                    particleCount={150}
                    width={1400}
                    colors={['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#F59E0B', '#10B981']}
                    duration={3000}
                  />
                </div>
              )}
              
              {/* Header Section */}
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-9 h-9 text-primary" />
                  </div>
                </div>
                
                <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
                  Additional Forms Complete! 🎉
                </AlertDialog.Title>
                
                <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  You've completed all the additional information forms for {getCypName()}.
                </AlertDialog.Description>
              </div>

              {/* What Happens Next Section */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6 rms-animate-fadeIn">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <ClipboardListIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      What happens next?
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      The referral for {getCypName()} is now ready to be reviewed by the team at {orgName}.                      
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      You'll be contacted with next steps once the referral has been reviewed.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                {/* If HFP is also complete, show View HFP button */}
                {showHfpCta && (
                  <button
                    type="button"
                    onClick={() => {
                      onViewHfp();
                      onClose();
                    }}
                    className="w-full py-3 px-4 bg-gradient-to-r from-primary to-teal-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <DocumentTextIcon className="w-5 h-5" />
                    View Holistic Functioning Profile
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={onClose}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    showHfpCta
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {showHfpCta ? 'Close' : 'Got it'}
                </button>
              </div>
            </div>
            
            {/* Close X button in top right */}
            <AlertDialog.Cancel asChild>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </AlertDialog.Cancel>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
